<template>
  <div class="syllable-trainer">
    <div v-if="!started" class="setup">
      <h2>Выбери режим</h2>

      <div class="setup-row">
        <span class="setup-label">Букв:</span>
        <button
          v-for="l in lengths"
          :key="l"
          :class="['time-btn', { active: chosenLength === l }]"
          @click="chosenLength = l"
        >
          {{ l }}
        </button>
      </div>

      <div class="setup-row">
        <span class="setup-label">Время:</span>
        <button
          v-for="m in durations"
          :key="m"
          :class="['time-btn', { active: chosenMinutes === m }]"
          @click="chosenMinutes = m"
        >
          {{ m }} мин
        </button>
      </div>

      <button class="btn-start" :disabled="!chosenMinutes || !chosenLength" @click="startGame">
        Начать
      </button>
    </div>

    <template v-else-if="!finished">
      <div class="ball-area">
        <div
          :key="ballKey"
          class="balloon"
          :class="[ballState, flyDir]"
          :style="{ background: ballColor }"
        >
          <div class="balloon-highlight"></div>
          <span class="ball-text">{{ current }}</span>
          <div class="balloon-knot"></div>
          <div class="balloon-string"></div>
        </div>
      </div>

      <div class="bottom-bar">
        <div class="hint">
          <kbd>+</kbd> / <span class="swipe-hint">свайп ➡</span> правильно &nbsp; <kbd>−</kbd> / <span class="swipe-hint">свайп ⬅</span> ошибка
        </div>

        <div class="level-line">
          Уровень {{ currentLevel }}
        </div>

        <div class="history">
          <span
            v-for="(item, i) in history"
            :key="i"
            class="history-chip"
            :style="{ backgroundColor: item.correct ? '#4caf50' : '#f44336' }"
          >
            {{ item.syllable }}
          </span>
        </div>
      </div>

      <div class="timer">{{ timerDisplay }}</div>
    </template>

    <div v-else class="results">
      <div v-if="showLevelUp" class="levelup-overlay" @click="showLevelUp = false">
        <div class="levelup-card">
          <div class="trophy">🏆</div>
          <div class="levelup-text">Уровень {{ currentLevel }}!</div>
          <div class="levelup-sub">25 правильных ответов</div>
        </div>
      </div>

      <div class="results-hero">
        <div class="results-emoji">🎉</div>
        <h2 class="results-praise">Ты молодец!</h2>

        <div class="stars-section">
          <div class="stars-row">
            <span
              v-for="i in levelStars"
              :key="'lv' + i"
              class="star star-level"
              :style="{ animationDelay: (i - 1) * 0.15 + 's' }"
            >⭐</span>
          </div>
          <div v-if="bonusStars > 0" class="stars-row">
            <span
              v-for="i in bonusStars"
              :key="'bn' + i"
              class="star star-bonus"
              :style="{ animationDelay: (levelStars + i - 1) * 0.15 + 's' }"
            >🌟</span>
          </div>
        </div>

        <div class="stars-legend">
          <span v-if="levelStars">⭐ × {{ levelStars }} за уровень</span>
          <span v-if="bonusStars">🌟 × {{ bonusStars }} за выдержку</span>
        </div>
      </div>

      <div class="results-details">
        <div class="results-stats">
          <p>Пройдено: <strong>{{ history.length }}</strong></p>
          <p>Правильных: <strong>{{ correctCount }}</strong></p>
          <p>Ошибок: <strong>{{ wrongCount }}</strong></p>
          <p>Уровень: <strong>{{ currentLevel }}</strong></p>
          <p v-if="avgTimeSec !== null">
            Среднее время: <strong>{{ avgTimeSec }} сек</strong>
          </p>
        </div>

        <div class="history final-history">
          <span
            v-for="(item, i) in history"
            :key="i"
            class="history-chip"
            :style="{ backgroundColor: item.correct ? '#4caf50' : '#f44336' }"
          >
            {{ item.syllable }}
          </span>
        </div>
      </div>

      <div class="results-actions">
        <button class="btn-start" @click="restart">Ещё раз</button>
        <router-link to="/" class="btn-home">На главную</router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useSyllableStats, useSyllableStats3 } from '../composables/useSyllableStats.js'
import { useSessionStats } from '../composables/useSessionStats.js'
import { useUserProfile } from '../composables/useUserProfile.js'
import Hammer from 'hammerjs'

const store2 = useSyllableStats()
const store3 = ref(null)
useSyllableStats3().then(s => { store3.value = s })
const { startSession, endSession } = useSessionStats()
const { addStars } = useUserProfile()

const lengths = [2, 3]
const durations = [1, 3, 5, 7, 10, 15]
const chosenLength = ref(null)
const chosenMinutes = ref(null)
const started = ref(false)
const finished = ref(false)
const showLevelUp = ref(false)

const current = ref('')
const history = ref([])
const recentSyllables = ref([])
const sessionCorrectTimes = ref([])

const syllableShownAt = ref(0)
const currentLevel = ref(1)
const correctInLevel = ref(0)

const ballKey = ref(0)
const ballState = ref('')
const ballColor = ref('')
const flyDir = ref('')

const BALL_COLORS = [
  'linear-gradient(135deg, #42b883, #2ecc71)',
  'linear-gradient(135deg, #3498db, #2980b9)',
  'linear-gradient(135deg, #f39c12, #e67e22)',
  'linear-gradient(135deg, #9b59b6, #8e44ad)',
  'linear-gradient(135deg, #1abc9c, #16a085)',
  'linear-gradient(135deg, #e84393, #d63384)',
  'linear-gradient(135deg, #00b894, #00a381)',
  'linear-gradient(135deg, #6c5ce7, #5f3dc4)',
  'linear-gradient(135deg, #fdcb6e, #f0b429)',
  'linear-gradient(135deg, #74b9ff, #0984e3)',
]

function pickBallColor() {
  ballColor.value = BALL_COLORS[Math.floor(Math.random() * BALL_COLORS.length)]
}

function animateBallIn() {
  ballState.value = 'entering'
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      ballState.value = ''
    })
  })
}

const secondsLeft = ref(0)
let timerInterval = null
let activeSession = null

let hammer = null

const store = computed(() => chosenLength.value === 3 ? store3.value : store2)

const timerDisplay = computed(() => {
  const m = Math.floor(secondsLeft.value / 60)
  const s = secondsLeft.value % 60
  return `${m}:${s.toString().padStart(2, '0')}`
})

const correctCount = computed(() => history.value.filter(h => h.correct).length)
const wrongCount = computed(() => history.value.filter(h => !h.correct).length)

const levelStars = computed(() => currentLevel.value)
const bonusStars = computed(() => {
  if (wrongCount.value > 0) return 0
  let bonus = 0
  if (chosenMinutes.value) bonus += Math.floor(chosenMinutes.value / 1)
  if (chosenLength.value === 3) bonus += Math.floor(chosenMinutes.value / 1)
  return bonus
})
const totalStars = computed(() => levelStars.value + bonusStars.value)

const avgTimeSec = computed(() => {
  const times = [...sessionCorrectTimes.value].sort((a, b) => a - b)
  if (times.length <= 4) {
    if (!times.length) return null
    const avg = times.reduce((s, t) => s + t, 0) / times.length
    return (avg / 1000).toFixed(1)
  }
  const trimmed = times.slice(2, -2)
  const avg = trimmed.reduce((s, t) => s + t, 0) / trimmed.length
  return (avg / 1000).toFixed(1)
})

function startGame() {
  if (!chosenMinutes.value || !chosenLength.value) return
  started.value = true
  finished.value = false
  showLevelUp.value = false
  history.value = []
  recentSyllables.value = []
  sessionCorrectTimes.value = []
  currentLevel.value = 1
  correctInLevel.value = 0
  secondsLeft.value = chosenMinutes.value * 60
  activeSession = startSession('syllables', { length: chosenLength.value, time: chosenMinutes.value })
  nextSyllable()
  document.body.style.overflow = 'hidden'
  document.body.style.touchAction = 'none'
  const hammerTarget = document.getElementById('app') || document.body
  console.log('[Hammer] creating on', hammerTarget.tagName, '#' + hammerTarget.id)
  hammer = new Hammer(hammerTarget, { touchAction: 'none', inputClass: Hammer.TouchInput })
  console.log('[Hammer] created', !!hammer)
  hammer.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 5, velocity: 0.1 })
  hammer.on('swipeleft', (ev) => { console.log('[Hammer] swipeleft', ev.deltaX); setTimeout(answerWrong, 0) })
  hammer.on('swiperight', (ev) => { console.log('[Hammer] swiperight', ev.deltaX); setTimeout(answerCorrect, 0) })
  hammer.on('panstart', () => console.log('[Hammer] panstart'))
  hammer.on('pan', (ev) => console.log('[Hammer] pan', ev.deltaX))
  const debugTouch = (e) => { console.log('[DEBUG] touch事件', e.type, e.touches?.length) }
  hammerTarget.addEventListener('touchstart', debugTouch, { passive: true })
  hammerTarget.addEventListener('touchend', debugTouch, { passive: true })
  hammerTarget.addEventListener('touchmove', debugTouch, { passive: true })
  hammer._debugTouch = debugTouch
  hammer._debugTarget = hammerTarget
  timerInterval = setInterval(() => {
    secondsLeft.value--
    if (secondsLeft.value <= 0) {
      clearInterval(timerInterval)
      endGame()
    }
  }, 1000)
}

function endGame() {
  finished.value = true
  window.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
  document.body.style.touchAction = ''
  if (hammer) {
    if (hammer._debugTouch && hammer._debugTarget) {
      hammer._debugTarget.removeEventListener('touchstart', hammer._debugTouch)
      hammer._debugTarget.removeEventListener('touchend', hammer._debugTouch)
      hammer._debugTarget.removeEventListener('touchmove', hammer._debugTouch)
    }
    hammer.destroy(); hammer = null
  }
  addStars(totalStars.value)
  if (activeSession) {
    activeSession.total = history.value.length
    activeSession.correct = history.value.filter(h => h.correct).length
    activeSession.wrong = history.value.filter(h => !h.correct).length
    activeSession.level = currentLevel.value
    if (sessionCorrectTimes.value.length > 4) {
      const t = [...sessionCorrectTimes.value].sort((a, b) => a - b).slice(2, -2)
      activeSession.avgTime = Math.round(t.reduce((s, v) => s + v, 0) / t.length / 100) / 10
    } else if (sessionCorrectTimes.value.length) {
      activeSession.avgTime = Math.round(sessionCorrectTimes.value.reduce((s, v) => s + v, 0) / sessionCorrectTimes.value.length / 100) / 10
    }
    endSession(activeSession)
    activeSession = null
  }
}

function nextSyllable() {
  current.value = store.value.pickNext(recentSyllables.value)
  syllableShownAt.value = Date.now()
  ballState.value = ''
  flyDir.value = ''
  ballKey.value++
  pickBallColor()
  animateBallIn()
}

function checkLevelUp() {
  if (correctInLevel.value >= 25) {
    currentLevel.value++
    correctInLevel.value = 0
    showLevelUp.value = true
    setTimeout(() => { showLevelUp.value = false }, 3500)
  }
}

function onKey(e) {
  if (!started.value || finished.value) return

  if (e.key === '+' || e.key === '=' || e.key === 'Equal') {
    answerCorrect()
  } else if (e.key === '-' || e.key === 'Minus') {
    answerWrong()
  }
}

function answerCorrect() {
  console.log('[answerCorrect] called, started=', started.value, 'finished=', finished.value)
  if (!started.value || finished.value) return
  try {
  const elapsed = Date.now() - syllableShownAt.value
  store.value.record(current.value, true, elapsed)
  history.value.push({ syllable: current.value, correct: true })
  sessionCorrectTimes.value.push(elapsed)
  recentSyllables.value.push(current.value)
  correctInLevel.value++
  checkLevelUp()
  const dirs = ['fly-up', 'fly-left', 'fly-right']
  flyDir.value = dirs[Math.floor(Math.random() * dirs.length)]
  ballState.value = 'flying'
  console.log('[answerCorrect] done, scheduling nextSyllable')
  setTimeout(() => {
    nextSyllable()
  }, 400)
  } catch (e) { console.error('[answerCorrect] ERROR', e) }
}

function answerWrong() {
  console.log('[answerWrong] called, started=', started.value, 'finished=', finished.value)
  if (!started.value || finished.value) return
  try {
  const elapsed = Date.now() - syllableShownAt.value
  store.value.record(current.value, false, elapsed)
  history.value.push({ syllable: current.value, correct: false })
  syllableShownAt.value = Date.now()
  ballState.value = 'wrong'
  console.log('[answerWrong] done')
  setTimeout(() => {
    ballState.value = ''
  }, 1000)
  } catch (e) { console.error('[answerWrong] ERROR', e) }
}

function restart() {
  finished.value = false
  started.value = false
}

onMounted(() => {
  window.addEventListener('keydown', onKey)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
  document.body.style.touchAction = ''
  if (hammer) {
    if (hammer._debugTouch && hammer._debugTarget) {
      hammer._debugTarget.removeEventListener('touchstart', hammer._debugTouch)
      hammer._debugTarget.removeEventListener('touchend', hammer._debugTouch)
      hammer._debugTarget.removeEventListener('touchmove', hammer._debugTouch)
    }
    hammer.destroy(); hammer = null
  }
  if (timerInterval) clearInterval(timerInterval)
})
</script>

<style scoped>
.syllable-trainer {
  max-width: 700px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 40px);
  padding-top: 20px;
}

.setup {
  padding-top: 60px;
}

.setup h2 {
  color: #2c3e50;
  margin-bottom: 24px;
}

.setup-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.setup-label {
  font-size: 18px;
  color: #555;
  font-weight: 700;
  min-width: 60px;
}

.time-btn {
  padding: 12px 20px;
  border: 2px solid #42b883;
  border-radius: 12px;
  background: white;
  color: #42b883;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.time-btn:hover {
  background: #42b883;
  color: white;
}

.time-btn.active {
  background: #42b883;
  color: white;
}

.btn-start {
  padding: 14px 40px;
  background: #42b883;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-start:hover {
  background: #369970;
}

.btn-start:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.ball-area {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 350px;
  touch-action: none;
}

.balloon {
  position: relative;
  width: 200px;
  height: 240px;
  border-radius: 50% 50% 50% 50% / 45% 45% 55% 55%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow:
    inset -15px -25px 40px rgba(0, 0, 0, 0.18),
    inset 8px 12px 25px rgba(255, 255, 255, 0.25),
    0 8px 30px rgba(0, 0, 0, 0.12);
  transition: background 0.6s ease, transform 0.3s ease, opacity 0.3s ease;
  animation: float 3s ease-in-out infinite;
}

.balloon-highlight {
  position: absolute;
  top: 22%;
  left: 22%;
  width: 35%;
  height: 30%;
  border-radius: 50%;
  background: radial-gradient(ellipse, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 70%);
  transform: rotate(-30deg);
  pointer-events: none;
}

.balloon-knot {
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 14px;
  height: 14px;
  background: rgba(0, 0, 0, 0.2);
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
}

.balloon-string {
  position: absolute;
  bottom: -100px;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  height: 92px;
  background: transparent;
  border-left: 2px dashed #aaa;
  pointer-events: none;
}

.balloon.entering {
  animation: ballEnter 0.5s ease forwards;
}

.ball-text {
  font-family: 'Nunito', 'Arial', sans-serif;
  font-size: 72px;
  font-weight: 900;
  color: white;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  user-select: none;
  z-index: 1;
}

.balloon.wrong {
  background: linear-gradient(135deg, #e74c3c, #c0392b) !important;
  animation: shake 0.4s ease;
}

.balloon.flying {
  pointer-events: none;
}

.balloon.fly-up {
  animation: flyUp 0.5s ease forwards;
}

.balloon.fly-left {
  animation: flyLeft 0.5s ease forwards;
}

.balloon.fly-right {
  animation: flyRight 0.5s ease forwards;
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-8px) rotate(1deg); }
  75% { transform: translateY(4px) rotate(-1deg); }
}

@keyframes ballEnter {
  0% { transform: scale(0) translateY(60px); opacity: 0; }
  50% { transform: scale(1.2) translateY(-15px); opacity: 1; }
  70% { transform: scale(0.95) translateY(5px); }
  100% { transform: scale(1) translateY(0); }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-12px); }
  40% { transform: translateX(12px); }
  60% { transform: translateX(-8px); }
  80% { transform: translateX(8px); }
}

@keyframes flyUp {
  0% { transform: translateY(0) scale(1); opacity: 1; }
  100% { transform: translateY(-350px) scale(0.3); opacity: 0; }
}

@keyframes flyLeft {
  0% { transform: translateX(0) translateY(0) scale(1) rotate(0deg); opacity: 1; }
  100% { transform: translateX(-300px) translateY(-120px) scale(0.3) rotate(-20deg); opacity: 0; }
}

@keyframes flyRight {
  0% { transform: translateX(0) translateY(0) scale(1) rotate(0deg); opacity: 1; }
  100% { transform: translateX(300px) translateY(-120px) scale(0.3) rotate(20deg); opacity: 0; }
}

.bottom-bar {
  margin-top: auto;
  padding-top: 20px;
}

.hint {
  font-size: 13px;
  color: #ccc;
  margin-bottom: 10px;
}

.hint kbd {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  background: #f7f7f7;
  font-family: monospace;
  font-size: 13px;
  color: #bbb;
}

.swipe-hint {
  font-size: 13px;
  color: #bbb;
}

.level-line {
  font-size: 12px;
  color: #ccc;
  margin-bottom: 10px;
}

.history {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: center;
  max-height: 100px;
  overflow-y: auto;
  padding: 4px 8px 8px;
}

.final-history {
  max-height: 300px;
  margin-bottom: 20px;
}

.history-chip {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 5px;
  color: white;
  font-size: 13px;
  font-weight: 700;
  opacity: 0.7;
}

.timer {
  position: fixed;
  bottom: 10px;
  right: 14px;
  font-size: 11px;
  color: #ccc;
  font-family: monospace;
}

.results {
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.results-hero {
  text-align: center;
  margin-bottom: 24px;
}

.results-emoji {
  font-size: 64px;
  animation: heroEmojiIn 0.6s ease;
}

@keyframes heroEmojiIn {
  0% { transform: scale(0) rotate(-20deg); opacity: 0; }
  50% { transform: scale(1.3) rotate(10deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.results-praise {
  font-size: 36px;
  font-weight: 900;
  background: linear-gradient(135deg, #f39c12, #e74c3c, #e84393);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 12px 0 24px;
  animation: praiseIn 0.8s ease;
}

@keyframes praiseIn {
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

.stars-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.stars-row {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
}

.star {
  display: inline-block;
  font-size: 40px;
  opacity: 0;
  animation: starPop 0.5s ease forwards;
}

@keyframes starPop {
  0% { transform: scale(0) rotate(-180deg); opacity: 0; }
  60% { transform: scale(1.3) rotate(10deg); opacity: 1; }
  80% { transform: scale(0.9) rotate(-5deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

.star-bonus {
  font-size: 32px;
}

.stars-legend {
  display: flex;
  gap: 16px;
  justify-content: center;
  font-size: 13px;
  color: #aaa;
  flex-wrap: wrap;
}

.results-details {
  width: 100%;
  max-width: 500px;
  border-top: 1px solid #eee;
  padding-top: 20px;
  margin-bottom: 16px;
}

.results-details h2 {
  color: #2c3e50;
  margin-bottom: 20px;
}

.results-stats {
  text-align: center;
  margin-bottom: 16px;
}

.results-stats p {
  font-size: 16px;
  color: #999;
  margin-bottom: 4px;
}

.results-stats strong {
  color: #777;
}

.results-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 20px;
}

.btn-home {
  padding: 14px 32px;
  background: #f0f0f0;
  color: #555;
  border-radius: 12px;
  text-decoration: none;
  font-size: 18px;
  font-weight: 700;
  transition: background 0.2s;
}

.btn-home:hover {
  background: #e0e0e0;
}

.levelup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.levelup-card {
  background: linear-gradient(135deg, #fff9c4, #fff176);
  padding: 40px 60px;
  border-radius: 24px;
  text-align: center;
  animation: popIn 0.5s ease;
  box-shadow: 0 8px 40px rgba(255, 193, 7, 0.4);
}

@keyframes popIn {
  0% { transform: scale(0.3); opacity: 0; }
  60% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

.trophy {
  font-size: 80px;
  animation: bounce 0.6s ease infinite alternate;
}

@keyframes bounce {
  from { transform: translateY(0); }
  to { transform: translateY(-10px); }
}

.levelup-text {
  font-size: 32px;
  font-weight: 900;
  color: #e65100;
  margin-top: 12px;
}

.levelup-sub {
  font-size: 16px;
  color: #bf360c;
  margin-top: 6px;
  opacity: 0.8;
}
</style>
